<template>
  <div class="banner-management">
    <Sidebar />
    
    <!-- Top Actions Bar -->
    <div class="top-bar">
      <div class="search-bar">
        <input 
          v-model="searchQuery"
          type="text" 
          placeholder="Search banners..."
          class="search-input"
        >
      </div>
      <button @click="showCreateForm" class="add-button">
        <span class="icon">+</span> Create New Banner
      </button>
    </div>

    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>

    <!-- Banner Grid -->
    <div v-else class="banner-grid">
      <div v-for="banner in filteredBanners" :key="banner.id" class="banner-card">
        <div class="card-actions">
          <button @click="showEditForm(banner)" class="action-button edit">Edit</button>
          <button @click="deleteBanner(banner.id)" class="action-button delete">Delete</button>
          <button @click="toggleActive(banner)" 
            class="action-button"
            :class="banner.is_active ? 'active' : 'inactive'">
            {{ banner.is_active ? 'Active' : 'Inactive' }}
          </button>
        </div>
        <img :src="banner.image_url" class="card-image" :alt="banner.title">
        <div class="card-content">
          <h3>{{ banner.title }}</h3>
          <p class="banner-order">Order: {{ banner.order }}</p>
          <p>{{ banner.description }}</p>
          <div v-if="banner.expiry_date" class="expiry-date">
            Expiry: {{ formatDate(banner.expiry_date) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="modal">
      <div class="modal-content">
        <h2>{{ isEditing ? 'Edit Banner' : 'Create Banner' }}</h2>
        <form @submit.prevent="saveBanner">
          <div class="form-group">
            <label>Title (English)</label>
            <input v-model="formData.title" type="text" required>
          </div>
          <div class="form-group">
            <label>Title (Arabic)</label>
            <input v-model="formData.title_ar" type="text" required>
          </div>
          <div class="form-group">
            <label>Description (English)</label>
            <QuillRichTextEditor
              v-model="formData.description"
              placeholder="Enter banner description"
              :minHeight="'150px'"
              required
            />
          </div>
          <div class="form-group">
            <label>Description (Arabic)</label>
            <QuillRichTextEditor
              v-model="formData.description_ar"
              placeholder="أدخل وصف البانر"
              :toolbar="arabicToolbar"
              :minHeight="'150px'"
              required
            />
          </div>
          <div class="form-group">
            <label>Image URL</label>
            <div class="file-uploader">
              <button @click="openUploadWidget('image_url')" class="upload-btn">
                <i class="fas fa-upload"></i> Upload Image
              </button>
              <div v-if="formData.image_url" class="uploaded-file">
                <img :src="formData.image_url" alt="Banner Image" class="preview-image">
              </div>
            </div>
          </div>
          <div class="form-group">
            <label>Arabic Image URL</label>
            <div class="file-uploader">
              <button @click="openUploadWidget('image_url_ar')" class="upload-btn">
                <i class="fas fa-upload"></i> Upload Arabic Image
              </button>
              <div v-if="formData.image_url_ar" class="uploaded-file">
                <img :src="formData.image_url_ar" alt="Arabic Banner Image" class="preview-image">
              </div>
            </div>
          </div>
          <div class="form-group">
            <label>Order</label>
            <input v-model.number="formData.order" type="number" required>
          </div>
          <div class="form-group">
            <label>Course</label>
            <select v-model.number="formData.course_id" required>
              <option v-for="course in courses" :value="course.id" :key="course.id">{{ course.title }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Expiry Date</label>
            <input v-model="formData.expiry_date" type="date">
          </div>
          <div class="form-group checkbox-group">
            <input v-model="formData.is_active" type="checkbox" id="is_active">
            <label for="is_active">Active</label>
          </div>
          <div class="form-actions">
            <button type="button" @click="closeModal" class="cancel-button">Cancel</button>
            <button type="submit" class="submit-button">Save</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';
import QuillRichTextEditor from './QuillEditor.vue';
import cloudflareR2Service from '@/services/cloudflareR2';
import { UPLOAD_CONFIG } from '@/config';

const API_URL = 'https://api.kewaar.com/api/admin/banners1';
const headers = {
  'Authorization': 'Bearer ' + localStorage.getItem('token'),
  'Content-Type': 'application/json'
};

export default {
  components: { Sidebar, QuillRichTextEditor },
  data() {
    return {
      searchQuery: '',
      loading: true,
      banners: [],
      courses: [],
      showModal: false,
      isEditing: false,
      uploadError: '',
      formData: {
        id: null,
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        image_url: '',
        image_url_ar: '',
        order: 0,
        course_id: null,
        expiry_date: null,
        is_active: true
      },
      arabicToolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ 'header': 1 }, { 'header': 2 }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'font': [] }],
        [{ 'align': ['', 'right', 'center', 'justify'] }],
        ['clean'],
        ['link', 'image', 'video']
      ],
    };
  },
  computed: {
    filteredBanners() {
      const query = this.searchQuery.toLowerCase();
      return this.banners.filter(banner => {
        return (
          banner.title.toLowerCase().includes(query) ||
          banner.title_ar.toLowerCase().includes(query) ||
          banner.description.toLowerCase().includes(query) ||
          banner.description_ar.toLowerCase().includes(query)
        );
      });
    }
  },
  async created() {
    await this.fetchBanners();
    await this.fetchCourses();
  },
  methods: {
    async fetchBanners() {
      try {
        const response = await axios.get(API_URL, { headers });
        this.banners = response.data.data;
        this.loading = false;
      } catch (error) {
        console.error('Error fetching banners:', error);
      }
    },
    async fetchCourses() {
      try {
        const response = await axios.get('https://api.kewaar.com/api/admin/courses', { headers });
        this.courses = response.data.data;
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    },
    showCreateForm() {
      this.resetForm();
      this.isEditing = false;
      this.showModal = true;
    },
    showEditForm(banner) {
      this.formData = {
        id: banner.id,
        title: banner.title,
        title_ar: banner.title_ar,
        description: banner.description,
        description_ar: banner.description_ar,
        image_url: banner.image_url,
        image_url_ar: banner.image_url_ar,
        order: banner.order,
        course_id: banner.course_id,
        expiry_date: banner.expiry_date,
        is_active: banner.is_active
      };
      this.isEditing = true;
      this.showModal = true;
    },
    async saveBanner() {
      try {
        this.loading = true;
        const url = this.isEditing 
          ? `${API_URL}/${this.formData.id}`
          : API_URL;

        const method = this.isEditing ? 'put' : 'post';
        
        await axios[method](url, this.formData, { headers });
        await this.fetchBanners();
        this.closeModal();
      } catch (error) {
        console.error('Error saving banner:', error.response?.data);
      } finally {
        this.loading = false;
      }
    },
    async deleteBanner(id) {
      if (confirm('Are you sure you want to delete this banner?')) {
        try {
          await axios.delete(`${API_URL}/${id}`, { headers });
          await this.fetchBanners();
        } catch (error) {
          console.error('Error deleting banner:', error);
        }
      }
    },
    async toggleActive(banner) {
      try {
        await axios.put(`${API_URL}/${banner.id}/toggle-active`, {}, { headers });
        await this.fetchBanners();
      } catch (error) {
        console.error('Error toggling active status:', error);
      }
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        id: null,
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        image_url: '',
        image_url_ar: '',
        order: 0,
        course_id: null,
        expiry_date: null,
        is_active: true
      };
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    },
    async openUploadWidget(field) {
      this.uploadError = "";
      
      // Create a file input element programmatically
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES.join(',')
      
      input.onchange = async (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        try {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(file)
          this.formData[field] = uploadResponse.fileUrl
        } catch (error) {
          this.uploadError = `Upload failed: ${error.message || "Unknown error"}`
        }
      }
      
      input.click()
    },
  }
};
</script>

<style scoped>
.banner-management {
  display: flex;
  min-height: 100vh;
  background: #f5f5f5;
}

.top-bar {
  position: fixed;
  top: 1rem;
  right: 2rem;
  left: calc(250px + 2rem);
  z-index: 100;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: flex-end;
}

.add-button {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: background-color 0.2s;
}

.add-button:hover {
  background: #45a049;
}

.banner-grid {
  flex: 1;
  padding: 2rem;
  margin-left: 250px;
  padding-top: 5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  align-items: start;
}

.banner-card {
  position: relative;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  height: fit-content;
}

.banner-card:hover {
  transform: translateY(-4px);
}

.card-actions {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;
}

.banner-card:hover .card-actions {
  opacity: 1;
}

.action-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s;
}

.action-button.edit {
  background: #2196F3;
  color: white;
}

.action-button.delete {
  background: #f44336;
  color: white;
}

.action-button.active {
  background: #4CAF50;
  color: white;
}

.action-button.inactive {
  background: #ff9800;
  color: white;
}

.card-image {
  width: 100%;
  aspect-ratio: 16/9;
  object-fit: cover;
}

.card-content {
  padding: 1.5rem;
}

.card-content h3 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  color: #333;
  font-weight: 600;
}

.banner-order {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.expiry-date {
  font-size: 0.875rem;
  color: #888;
  margin-top: 1rem;
}

/* Add proper modal styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.form-actions {
  margin-top: 1.5rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.search-bar {
  flex-grow: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #2196F3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.file-uploader {
  margin-bottom: 1rem;
}

.upload-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  font: inherit;
  outline: inherit;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.uploaded-file {
  margin-top: 10px;
  width: 100%;
}

.uploaded-file img {
  width: 100%;
  height: auto;
  max-width: 300px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.upload-zone {
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.upload-zone:hover {
  border-color: #4CAF50;
  background: #f0fdf4;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.upload-zone:active {
  transform: translateY(0);
  box-shadow: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: #6b7280;
  padding: 1rem;
  width: 100%;
}

.upload-placeholder svg {
  color: #4CAF50;
  width: 32px;
  height: 32px;
}

.upload-placeholder span {
  font-size: 0.875rem;
  font-weight: 500;
}

.image-preview {
  margin-top: 1rem;
  border-radius: 8px;
  overflow: hidden;
  max-width: 300px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.image-preview img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: block;
}
</style> 